import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { useAppServices } from "./AppContext.tsx";
import { emptySnapshot } from "../../domain/vocabulary/types.ts";
import { isUsableStorageStatus } from "../../application/ports/storage.ts";

export function useStorageStatus() {
  const { store } = useAppServices();
  return useSyncExternalStore(
    (listener) => store.subscribeStatus(() => listener()),
    () => store.getStatus(),
    () => store.getStatus(),
  );
}

export function useVocabularySnapshot() {
  const status = useStorageStatus();
  if (isUsableStorageStatus(status)) {
    return status.snapshot;
  }
  return emptySnapshot();
}

export function useGeneration() {
  const { generation } = useAppServices();
  return useSyncExternalStore(generation.subscribe, generation.getSnapshot, generation.getSnapshot);
}

export function usePractice() {
  const { practice } = useAppServices();
  return useSyncExternalStore(practice.subscribe, practice.getSnapshot, practice.getSnapshot);
}

export function useImport() {
  const { importer } = useAppServices();
  return useSyncExternalStore(importer.subscribe, importer.getSnapshot, importer.getSnapshot);
}

export function useSettingsState() {
  const { settingsController } = useAppServices();
  return useSyncExternalStore(
    settingsController.subscribe,
    settingsController.getSnapshot,
    settingsController.getSnapshot,
  );
}

export function useOnline() {
  const { platform } = useAppServices();
  return useSyncExternalStore(
    (listener) => platform.network.subscribe(listener),
    () => platform.network.isOnline(),
    () => platform.network.isOnline(),
  );
}

export function useUpdateState() {
  const { platform } = useAppServices();
  return useSyncExternalStore(
    (listener) => platform.updates.subscribe(listener),
    () => platform.updates.getState(),
    () => platform.updates.getState(),
  );
}

export function useNow(intervalMs = 60_000): string {
  const { platform } = useAppServices();
  const [now, setNow] = useState(() => platform.clock.now());
  useEffect(() => {
    const tick = () => setNow(platform.clock.now());
    tick();
    const id = window.setInterval(tick, intervalMs);
    window.addEventListener("focus", tick);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("focus", tick);
    };
  }, [platform, intervalMs]);
  return now;
}

export function useHomeModel() {
  const snapshot = useVocabularySnapshot();
  const { queries, platform } = useAppServices();
  const now = useNow();
  return useMemo(
    () => ({
      greeting: platform.clock.todayGreeting(),
      counts: queries.counts(now),
      recent: queries.recentGroups(),
      snapshot,
      now,
    }),
    [queries, platform, snapshot, now],
  );
}
